import { useCallback } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { TransactionTitle } from '~/components/component-library/TransactionTitle'
import { ApproveTransactionDialogContent } from '~/components/smart-accounts/ApproveTransactionDialogContent'
import { SupportedChainId } from '~/constants/chains'
import { QUERY_KEYS } from '~/constants/react-query'
import { useAuth } from '~/context/AuthProvider'
import { useHandleMerkleTransactionError } from '~/hooks/merkle-transactions/useHandleMerkleTransactionError'
import { useActiveOrgId, useActiveOrgUpdates } from '~/hooks/org'
import { useNavigation } from '~/hooks/useNavigation'
import {
  getSplitsErrorCodeString,
  useServerRequests,
} from '~/hooks/useRequests'
import { OrgMerkleTransaction, OrgTransaction } from '~/types'

type UpdateOrgNetworksTransactionData = {
  orgId: string
  memo?: string
  chainIds: SupportedChainId[]
  onSuccess?: (transaction?: OrgMerkleTransaction) => void
  onError?: () => void
}

export const useUpdateOrgNetworks = (): {
  createUpdateOrgNetworksTransaction: (
    txData: UpdateOrgNetworksTransactionData,
  ) => Promise<OrgMerkleTransaction | undefined>
  isProcessing: boolean
} => {
  const { isProcessing, serverPostRequest } = useServerRequests()
  const queryClient = useQueryClient()
  const { setGlobalModalState } = useAuth()
  const activeOrgId = useActiveOrgId()
  const { updateSmartAccountsChainIds } = useActiveOrgUpdates()

  const handleMerkleTransactionError = useHandleMerkleTransactionError()
  const navigate = useNavigation()
  const createUpdateOrgNetworksTransaction = useCallback(
    async (txData: UpdateOrgNetworksTransactionData) => {
      const transactionData = {
        orgId: txData.orgId,
        chainIds: txData.chainIds,
        memo: txData.memo,
      }

      const result = await serverPostRequest<
        | {
            didUpdateNetworks: true
            chainIds: SupportedChainId[]
          }
        | {
            didUpdateNetworks: false
            merkleTransaction: OrgMerkleTransaction
            transactions: OrgTransaction[]
          }
      >({
        path: '/api/smart_accounts/transactions/merkle/update_networks',
        params: {
          ...transactionData,
        },
        errorMessage: 'Failed to update networks',
        overrideErrorMessageOptions: handleMerkleTransactionError,
      })

      if (result) {
        if (result.data.didUpdateNetworks) {
          updateSmartAccountsChainIds(result.data.chainIds)
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.ACTIVE_ORG],
            predicate: (query) => {
              return (
                Array.isArray(query.queryKey) &&
                query.queryKey[1]?.activeOrgId === transactionData.orgId
              )
            },
          })

          toast.success('Networks updated')
          txData.onSuccess?.()
        } else {
          const merkleTransaction = result.data.merkleTransaction
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.TRANSACTIONS],
            predicate: (query) => {
              return (
                Array.isArray(query.queryKey) &&
                query.queryKey[1]?.smartAccountId ===
                  merkleTransaction.smartAccountId
              )
            },
          })
          queryClient.setQueryData(
            [QUERY_KEYS.TRANSACTION, activeOrgId, merkleTransaction.id],
            merkleTransaction,
          )

          toast.success('Transaction created', {
            id: merkleTransaction.id,
            description: (
              <TransactionTitle transaction={merkleTransaction} unstyled />
            ),
          })

          setGlobalModalState({
            isOpen: true,
            content: (
              <ApproveTransactionDialogContent
                transaction={merkleTransaction}
                globalModal
                onClose={() => {
                  setGlobalModalState({ isOpen: false, content: null })
                }}
                onSubmitError={(e) => {
                  const isResyncTransactionCreated =
                    getSplitsErrorCodeString(e) ===
                    'MERKLE_TRANSACTION_SUBMISSION_FAILED'

                  if (isResyncTransactionCreated) {
                    queryClient.invalidateQueries({
                      queryKey: [QUERY_KEYS.TRANSACTIONS],
                    })
                    navigate(
                      `/accounts/${merkleTransaction.smartAccountAddress}/`,
                    )
                  }
                }}
              />
            ),
          })

          txData.onSuccess?.(merkleTransaction)
          return merkleTransaction
        }
      } else {
        txData.onError?.()
      }
    },
    [
      queryClient,
      serverPostRequest,
      setGlobalModalState,
      updateSmartAccountsChainIds,
      activeOrgId,
      handleMerkleTransactionError,
      navigate,
    ],
  )

  return {
    createUpdateOrgNetworksTransaction,
    isProcessing,
  }
}
